import { useCallback, useEffect, useRef } from 'react'

type TimeoutID = ReturnType<typeof setTimeout>
type IntervalID = ReturnType<typeof setInterval>

export const useManagedTimers = () => {
    const timeoutIDs = useRef<Set<TimeoutID>>(new Set())
    const intervalIDs = useRef<Set<IntervalID>>(new Set())

    const clearManagedTimeout = useCallback((id?: TimeoutID | null) => {
        if (!id) return

        clearTimeout(id)
        timeoutIDs.current.delete(id)
    }, [])

    const clearManagedInterval = useCallback((id?: IntervalID | null) => {
        if (!id) return

        clearInterval(id)
        intervalIDs.current.delete(id)
    }, [])

    const setManagedTimeout = useCallback((callback: () => void, delay: number) => {
        let timeoutID: TimeoutID

        timeoutID = setTimeout(() => {
            timeoutIDs.current.delete(timeoutID)
            callback()
        }, delay)

        timeoutIDs.current.add(timeoutID)

        return timeoutID
    }, [])

    const setManagedInterval = useCallback((callback: () => void, delay: number) => {
        const intervalID = setInterval(callback, delay)
        intervalIDs.current.add(intervalID)

        return intervalID
    }, [])

    const clearAllTimers = useCallback(() => {
        timeoutIDs.current.forEach((id) => clearTimeout(id))
        intervalIDs.current.forEach((id) => clearInterval(id))

        timeoutIDs.current.clear()
        intervalIDs.current.clear()
    }, [])

    useEffect(() => clearAllTimers, [clearAllTimers])

    return {
        setManagedTimeout,
        clearManagedTimeout,
        setManagedInterval,
        clearManagedInterval,
        clearAllTimers,
    }
}